
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { auth } from "./firebase";
import { onAuthStateChanged, setPersistence, browserLocalPersistence } from "firebase/auth";
import { db } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

import Navbar from "./components/Navbar";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import AdminReferralCodes from "./pages/AdminReferralCodes";
import ReferralUsersList from "./pages/ReferralUsersList";
import AddAdmin from "./pages/AddAdmin";
import AdminCompleteSignup from "./pages/AdminCompleteSignup";
import ProtectedRoute from "./components/ProtectedRoute";
import InstructorList from "./pages/InstructorList";
import OrdersReview from "./pages/OrdersReview";
import AdminPackageSalesSummary from "./pages/AdminPackageSalesSummary";
import ProductOrderReview from "./pages/ProductOrderReview";
import BuyerInsights from "./pages/BuyerInsights";
import AdminBankSettings from "./pages/AdminBankSettings";
import ServiceRequestsReview from "./pages/ServiceRequestsReview";
import InstructorPackages from "./pages/InstructorPackages";
import InstructorPackageOrders from "./pages/InstructorPackageOrders";
import AffiliateSettlementsAdmin from "./pages/AffiliateSettlementsAdmin";
import ListingCategorySettings from "./pages/ListingCategorySettings";
import StoreSponsorships from "./pages/StoreSponsorships";
import StorefrontTemplates from "./pages/StorefrontTemplates";
import { useAuth } from "./AuthProvider";
import ShopOwnersSummary from "./pages/ShopOwnersSummary";
import VirtualMallSettings from "./pages/VirtualMallSettings";

const HIDE_LAYOUT_PATHS = ["/login", "/admin-complete-signup"];
const NAV_H = "h-16"; // keep in sync with Navbar height (e.g., 64px)
const NAV_H_PX = 64;  // same value in px

function Layout({ children }) {
  const location = useLocation();
  const { user, isAdminAuthorized } = useAuth();

  useEffect(() => {
    if (!user || !isAdminAuthorized) return;

    let cancelled = false;

    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "referral_users"), where("status", "==", "approved")),
        );

        if (cancelled) return;

        await Promise.all(
          snap.docs.map((entry) => {
            const row = entry.data() || {};
            const code = String(row.referralCode || "").trim();
            if (!code) return Promise.resolve();

            return setDoc(
              doc(db, "referral_links", code),
              {
                ownerUserId: entry.id,
                referralCode: code,
                fullName: row.fullName || "",
                email: row.email || "",
                level: Number(row.level || 0) || null,
                referredBy: row.referredBy || "",
                status: "active",
                updatedAt: serverTimestamp(),
              },
              { merge: true },
            );
          }),
        );
      } catch (error) {
        console.error("referral link sync failed", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isAdminAuthorized]);

  if (HIDE_LAYOUT_PATHS.includes(location.pathname)) return <>{children}</>;
  if (!user || !isAdminAuthorized) return <>{children}</>;

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Fixed Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-56 z-30">
        <Sidebar />
      </aside>

      {/* Fixed Navbar offset by sidebar */}
      <div
        className={`fixed top-0 left-64 right-0 z-40 bg-white/80 backdrop-blur border-b ${NAV_H}`}
      >
        <Navbar />
      </div>

      {/* Content column (offset by sidebar) with top padding = navbar height */}
      <div
        className="ml-56 min-w-0 min-h-screen"
        style={{ paddingTop: NAV_H_PX }}
      >
        <main className="py-6 px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * AuthGate
 * - Ensures Firebase restores the session before rendering routes
 * - Sets persistence to browserLocal so refreshes keep the session
 */
function AuthGate({ children }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Make sure auth is persistent across tabs/refresh
    setPersistence(auth, browserLocalPersistence).catch(() => {
      // ignore (already set, or environment not supporting)
    });

    const unsub = onAuthStateChanged(auth, () => {
      // once we get the first callback, auth state is hydrated
      setReady(true);
    });
    return () => unsub();
  }, []);

  if (!ready) {
    // Simple splash while Firebase hydrates
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600 text-sm">Loading…</div>
      </div>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <Router>
      <AuthGate>
        <Layout>
          <Routes>
            <Route path="/login" element={<Login />} />
            {/* <Route path="/add-admin" element={<AddAdmin />} /> */}
            <Route path="/admin-complete-signup" element={<AdminCompleteSignup />} />
            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-referral-codes"
              element={
                <ProtectedRoute>
                  <AdminReferralCodes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/referral-users"
              element={
                <ProtectedRoute>
                  <ReferralUsersList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor-users"
              element={
                <ProtectedRoute>
                  <InstructorList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders-review"
              element={
                <ProtectedRoute>
                  <OrdersReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-order-review"
              element={
                <ProtectedRoute>
                  <ProductOrderReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales"
              element={
                <ProtectedRoute>
                  <AdminPackageSalesSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-sales"
              element={
                <ProtectedRoute>
                  <ShopOwnersSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/shop-owners-summary"
              element={
                <ProtectedRoute>
                  <ShopOwnersSummary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/add-admin"
              element={
                <ProtectedRoute>
                  <AddAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buyer-insights"
              element={
                <ProtectedRoute>
                  <BuyerInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/banks-settings"
              element={
                <ProtectedRoute>
                  <AdminBankSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/services-review"
              element={
                <ProtectedRoute>
                  <ServiceRequestsReview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor-packages"
              element={
                <ProtectedRoute>
                  <InstructorPackages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/package-orders"
              element={
                <ProtectedRoute>
                  <InstructorPackageOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor-packages-orders"
              element={
                <ProtectedRoute>
                  <InstructorPackageOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listing-categories"
              element={
                <ProtectedRoute>
                  <ListingCategorySettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/affiliate-settlements"
              element={
                <ProtectedRoute>
                  <AffiliateSettlementsAdmin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/store-sponsorships"
              element={
                <ProtectedRoute>
                  <StoreSponsorships />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storefront-templates"
              element={
                <ProtectedRoute>
                  <StorefrontTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/virtual-mall-settings"
              element={
                <ProtectedRoute>
                  <VirtualMallSettings />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Layout>
      </AuthGate>
    </Router>
  );
}

export default App;
